import { PLACE_ID_PATTERN } from '@bandido/shared/types';
import type { RuteroOccupancy } from '@bandido/shared/types';
import {
  CatalogEntry,
  CatalogFreshness,
  CircuitCatalogMeta,
  CircuitDay,
  CircuitsSearchPayload,
  PricedSearchResult,
} from './queries';
import { PlaceRef } from './places';

/**
 * Helpers del vertical de circuitos (viajes guiados multi-día con salidas
 * datadas).
 *
 * El problema central del catálogo: el proveedor publica ~25.000 productos que
 * MEZCLAN temporadas viejas y vigentes (2010 en la misma lista que 2026), y
 * solo los de temporada vigente tienen salidas. Elegir uno viejo produce una
 * búsqueda sin resultados que parece un error del sistema, así que el
 * autocomplete prioriza y filtra por temporada.
 */

export type CircuitOffer = PricedSearchResult & {
  title?: string;
  description?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  durationDays?: number;
};

/** Temporada declarada por el proveedor ("2026"); 0 si no la publica. */
export function seasonOf(entry: CatalogEntry): number {
  const season = Number(entry.meta?.temporada);
  return Number.isFinite(season) ? season : 0;
}

/** Serie / línea de producto ("CLASICA", "TURISTA"). */
export function seriesOf(entry: CatalogEntry): string | undefined {
  const serie = entry.meta?.serie;
  return typeof serie === 'string' ? serie : undefined;
}

export interface CircuitProductUrlParams {
  circuito?: string;
  circuitoNombre?: string;
  /**
   * Proveedor del circuito confirmado. NO es display-only: el `productRef` es
   * provider-scoped, así que viaja al search como filtro `providers` — sin él,
   * dos proveedores con el mismo ref devuelven salidas del producto equivocado
   * o mezcladas. Va en la URL para que un link compartido o restaurado siga
   * apuntando al MISMO producto.
   */
  circuitoProveedor?: string;
  temporada?: string;
  /** Display-only metadata; it is not part of the Rutero search payload. */
  serie?: string;
}

/** URL fields that bind catalog display metadata to the committed product. */
export function circuitProductUrlParams(
  entry: CatalogEntry | null,
): CircuitProductUrlParams {
  return {
    circuito: entry?.id,
    circuitoNombre: entry?.name,
    circuitoProveedor: entry?.provider,
    temporada: entry && seasonOf(entry) ? String(seasonOf(entry)) : undefined,
    serie: entry ? seriesOf(entry) : undefined,
  };
}

/** Reconstructs the committed catalog display context from URL metadata. */
export function circuitProductFromUrlParams(
  params: CircuitProductUrlParams,
): CatalogEntry | null {
  if (!params.circuito) return null;
  const meta = {
    ...(params.temporada ? { temporada: params.temporada } : {}),
    ...(params.serie ? { serie: params.serie } : {}),
  };
  return {
    id: params.circuito,
    name: params.circuitoNombre ?? params.circuito,
    ...(params.circuitoProveedor
      ? { provider: params.circuitoProveedor }
      : {}),
    ...(Object.keys(meta).length ? { meta } : {}),
  };
}

/**
 * El payload de la búsqueda DIRIGIDA, derivado de la URL confirmada (que es la
 * autoridad del result set). Vive acá y no en el formulario porque es la
 * definición de qué producto se cotiza: el ref del circuito Y su proveedor,
 * que el hub necesita junto al ref porque este es provider-scoped.
 */
export function circuitSearchPayloadFromParams(params: {
  buscar?: string;
  circuito?: string;
  circuitoProveedor?: string;
  desde?: string;
  hasta?: string;
  pax?: number;
  occupancy?: RuteroOccupancy;
}): CircuitsSearchPayload | null {
  if (params.buscar !== '1' || !params.circuito || !params.desde) return null;
  return {
    productRef: params.circuito,
    ...(params.circuitoProveedor
      ? { providers: [params.circuitoProveedor] }
      : {}),
    from: params.desde,
    ...(params.hasta ? { to: params.hasta } : {}),
    pax: params.pax,
    ...(params.occupancy ? { occupancy: params.occupancy } : {}),
    lang: 'ES',
  };
}

// ── Ciudades del multibuscador (PLACES, epic #2167) ────────────────────────
// El filtro `cities` del catálogo (`useCircuitCatalog`) es AND y multi-select,
// a diferencia del resto de los verticales (un solo destino) — por eso NO
// reusa `PlaceAutocomplete` (single-select) sino un componente propio
// (`CircuitDestinationsPlaceInput`). La lista viaja en `ciudades`; cada entrada
// es `plc_*:nombre`, con
// el nombre URL-encodeado — un nombre de lugar puede traer comas (p.ej.
// "Washington, D.C.") y el separador de la lista es coma, así que sin
// encodeo el split rompería la alineación.

/** Reconoce una entrada de modo PLACES (`plc_*:nombre`) sin decodificarla. */
function isPlaceCityEntry(entry: string): boolean {
  const separator = entry.indexOf(':');
  return separator > 0 && PLACE_ID_PATTERN.test(entry.slice(0, separator));
}

function parseCityEntries(cities: string): string[] {
  return (cities ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** Serializa un lugar elegido para viajar en la lista de `ciudades`. */
export function circuitPlaceCityToParam(place: PlaceRef): string {
  return `${place.id}:${encodeURIComponent(place.name)}`;
}

/**
 * `decodeURIComponent` tira `URIError` ante un `%` truncado a mitad de
 * escape — plausible acá: es EXACTAMENTE la clase de URL recortada a mano
 * que el resto de esta función ya está pensada para tolerar (ver el
 * fallback al id sin nombre). Sin este guard, un link cortado no solo falla
 * de reconstruir bien: tira una excepción DENTRO de un `useMemo`/inicializador
 * de estado que corre en cada render, y esta app no tiene Error Boundary —
 * se lleva puesta la pantalla entera, no solo el buscador de circuitos.
 */
function safeDecodeCityName(value: string, fallback: string): string {
  try {
    return decodeURIComponent(value) || fallback;
  } catch {
    return fallback;
  }
}

/**
 * Ciudades reconstruidas del modo PLACES: `[]` salvo que TODAS las entradas
 * tengan forma de par places. Sin nombre en la entrada (URL recortada a
 * mano), el id sirve de nombre — mismo fallback que el resto del epic.
 */
export function circuitPlaceCitiesFromParams(cities: string): PlaceRef[] {
  const entries = parseCityEntries(cities);
  if (!entries.length || !entries.every(isPlaceCityEntry)) return [];
  return entries.map((entry) => {
    const separator = entry.indexOf(':');
    const id = entry.slice(0, separator);
    const name = safeDecodeCityName(entry.slice(separator + 1), id);
    return { id, name, breadcrumb: name, kind: 'city' as const };
  });
}

export type CircuitCatalogCitiesFilter =
  | { status: 'absent'; cities: [] }
  | { status: 'valid'; cities: string[] }
  | { status: 'invalid'; cities: [] };

/** Distingue ausencia de filtro de una referencia legacy irrecuperable. */
export function circuitCatalogCitiesFilterFromParams(
  citiesParam: string | undefined,
): CircuitCatalogCitiesFilter {
  if (citiesParam === undefined) return { status: 'absent', cities: [] };
  const places = circuitPlaceCitiesFromParams(citiesParam);
  if (!places.length) return { status: 'invalid', cities: [] };
  return { status: 'valid', cities: places.map((place) => place.id) };
}

export interface CircuitCatalogQuery {
  cities: string[];
  minDays?: number;
  maxDays?: number;
}

/** Decide si una URL comprometida puede disparar el descubrimiento. */
export function circuitCatalogQueryFromParams({
  buscar,
  circuito,
  ciudades,
  minDays,
  maxDays,
}: {
  buscar?: string;
  circuito?: string;
  ciudades?: string;
  minDays: number;
  maxDays: number;
}): CircuitCatalogQuery | null {
  if (buscar !== '1' || circuito) return null;
  const cityFilter = circuitCatalogCitiesFilterFromParams(ciudades);
  if (cityFilter.status === 'invalid') return null;
  if (!cityFilter.cities.length && !minDays && !maxDays) return null;
  return {
    cities: cityFilter.cities,
    ...(minDays ? { minDays } : {}),
    ...(maxDays ? { maxDays } : {}),
  };
}

/**
 * Vigente = temporada del año en curso o posterior. Un circuito de temporada
 * anterior puede existir en el catálogo y no tener ninguna salida futura.
 */
export function isCurrentSeason(entry: CatalogEntry, currentYear: number): boolean {
  return seasonOf(entry) >= currentYear;
}

/**
 * Ordena para el autocomplete: primero las temporadas más nuevas (que son las
 * que tienen salidas) y, dentro de cada una, alfabético.
 */
export function sortProducts(entries: CatalogEntry[]): CatalogEntry[] {
  return [...entries].sort((a, b) => {
    const bySeason = seasonOf(b) - seasonOf(a);
    return bySeason !== 0 ? bySeason : a.name.localeCompare(b.name);
  });
}

/**
 * El nombre del catálogo trae la serie pegada al final ("Bellezas de Europa
 * Fin Roma - TURISTA"): se separa para no repetirla junto al chip de serie.
 */
export function productTitle(entry: CatalogEntry): string {
  const serie = seriesOf(entry);
  if (serie && entry.name.endsWith(` - ${serie}`)) {
    return entry.name.slice(0, -(serie.length + 3));
  }
  return entry.name;
}

/** "2026-09-02" → "02/09/2026" (fecha suelta, sin zona horaria). */
export function formatDate(value?: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return day ? `${day}/${month}/${year}` : value;
}

/**
 * Cómo se rotula el precio de una salida. Es una decisión con plata adentro y
 * vive acá, testeada, en vez de en el JSX.
 *
 * El `priceBasis` del contrato describe el precio CRUDO del proveedor (por
 * persona en doble, o total). Lo que la card muestra en grande NO es ese
 * número: el backend totaliza los verticales que cotizan por pasajero
 * (`PER_PASSENGER_VERTICALS`, circuitos incluido) y deja el unitario aparte en
 * `unitPrice`. Rotular el total con el basis del proveedor le pone "p/p en
 * doble" al importe de dos pasajeros — el asesor cotizaría el doble.
 *
 * Entonces: si hay `unitPrice`, el grande es el TOTAL de los pasajeros
 * buscados y el basis describe al unitario. Si no hay `unitPrice`, total y
 * unitario son el mismo número y el basis lo describe. Un basis que no está en
 * el enum del contrato no se rotula.
 */
export type CircuitPriceLabel =
  | { key: 'perPersonDouble' }
  | { key: 'totalForPax'; count: number }
  | undefined;

function basisLabel(
  priceBasis: string | undefined,
  searchedPax: number,
): CircuitPriceLabel {
  if (priceBasis === 'per-person-double') return { key: 'perPersonDouble' };
  if (priceBasis === 'total') return { key: 'totalForPax', count: searchedPax };
  return undefined;
}

export function circuitPriceLabels(
  offer: Pick<CircuitOffer, 'priceBasis' | 'unitPrice'>,
  searchedPax: number,
): { total: CircuitPriceLabel; unit: CircuitPriceLabel } {
  if (offer.unitPrice) {
    return {
      total: { key: 'totalForPax', count: searchedPax },
      // Solo un basis POR PERSONA describe al unitario. Con `total` no hay
      // unitario que rotular (el backend ya no lo produce, y si llegara uno,
      // rotularlo "total por N" mostraría dos importes como el mismo total).
      unit:
        offer.priceBasis === 'per-person-double'
          ? { key: 'perPersonDouble' }
          : undefined,
    };
  }
  return { total: basisLabel(offer.priceBasis, searchedPax), unit: undefined };
}

/**
 * La fecha de una SALIDA, con el día de la semana adelante ("mar 8 sep"): la
 * salida de un circuito cae siempre el mismo día de la semana y es lo primero
 * que mira el asesor cuando arma el viaje alrededor.
 *
 * Se construye la fecha en hora LOCAL (`new Date(y, m - 1, d)`): parsear
 * "2026-09-08" con `new Date(string)` la interpreta en UTC y en cualquier zona
 * al oeste de Greenwich la salida se pinta un día antes.
 */
export function departureDateLabel(value?: string, locale = 'es'): string {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** Duración publicada, o derivada de las fechas de la salida. */
export function circuitDuration(offer: CircuitOffer): number | undefined {
  if (typeof offer.durationDays === 'number' && offer.durationDays > 0) {
    return offer.durationDays;
  }
  if (!offer.startDate || !offer.endDate) return undefined;
  const from = Date.parse(`${offer.startDate}T00:00:00Z`);
  const to = Date.parse(`${offer.endDate}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return undefined;
  // Un circuito de ida y vuelta el mismo día dura 1, no 0.
  return Math.round((to - from) / 86_400_000) + 1;
}

/** Salidas ordenadas por fecha: el agente elige cuándo sale, no qué es. */
export function sortDepartures(offers: CircuitOffer[]): CircuitOffer[] {
  return [...offers].sort((a, b) =>
    (a.startDate ?? '').localeCompare(b.startDate ?? ''));
}

// ── Filtros de resultados ───────────────────────────────────────────────────
// Circuitos tiene DOS listas y por eso dos juegos de filtros: la grilla de
// descubrimiento (catálogo, sin cotizar) y las salidas de un circuito ya
// elegido. Comparten la mecánica del resto de los motores —filtrado local con
// conteo por faceta— pero no los criterios, porque no responden lo mismo.

/** Bandas de duración de la grilla, en días. El último es abierto. */
export const DURATION_BANDS = [
  { key: '1-7', min: 1, max: 7 },
  { key: '8-14', min: 8, max: 14 },
  { key: '15+', min: 15, max: Number.MAX_SAFE_INTEGER },
] as const;

export interface CatalogFilters {
  providers: string[];
  /** Claves de `DURATION_BANDS`. */
  durations: string[];
}

export const EMPTY_CATALOG_FILTERS: CatalogFilters = {
  providers: [],
  durations: [],
};

/** Lo que el backend dejó pasar del `meta` del catálogo, ya saneado y priceado. */
export function catalogMeta(entry: CatalogEntry): CircuitCatalogMeta {
  return (entry.meta ?? {}) as CircuitCatalogMeta;
}

/**
 * Tope de ciudades de la cadena. El catálogo NO trae itinerario —las noches
 * por ciudad se derivan del día a día de la ficha, no de acá—, así que la
 * cadena es el único indicio de por dónde pasa el circuito. Se muestra lo que
 * entra en una línea (la densidad de la lista es criterio de aceptación) y que
 * sigue se DICE con "…", en vez de recortar en silencio.
 */
export const CATALOG_CITY_CHAIN_MAX = 6;

export function catalogCityChain(
  entry: CatalogEntry,
  max = CATALOG_CITY_CHAIN_MAX,
): { cities: string[]; truncated: boolean } {
  const cities = catalogMeta(entry).cities ?? [];
  return { cities: cities.slice(0, max), truncated: cities.length > max };
}

// ── Cadena de ciudades DERIVADA del itinerario (ficha) ─────────────────────

/** Una parada del recorrido, con cuántos días seguidos para el circuito ahí. */
export interface ItineraryStop {
  city: string;
  /**
   * Días CONSECUTIVOS del itinerario con ese título. El contrato publica días,
   * no noches: se cuenta lo que trae, no se deriva una noche que nadie declaró.
   */
  days: number;
}

/**
 * Tope de la cadena de la ficha. Mismo criterio que el catálogo
 * (`CATALOG_CITY_CHAIN_MAX`): lo que entra en una línea, y que sigue se DICE.
 */
export const ITINERARY_CITY_CHAIN_MAX = 6;

/** Un título de día largo ya no es un topónimo, es una frase. */
const MAX_CITY_TITLE = 40;

/**
 * ¿El título del día se puede leer como el nombre de la parada? Europamundo
 * escribe la ciudad ("Lisboa"), pero no siempre: hay productos con frases
 * ("Llegada, traslado al hotel y tiempo libre") y otros con números.
 */
function looksLikeCity(title: string): boolean {
  if (title.length > MAX_CITY_TITLE) return false;
  // Puntuación de oración y dígitos: es un relato o un "Día 3", no una ciudad.
  if (/[.:;!?¡¿]/.test(title)) return false;
  if (/\d/.test(title)) return false;
  return title.split(/\s+/).length <= 4;
}

/**
 * El contrato no publica "por dónde pasa" el circuito: publica un título por
 * día. La cadena de la ficha se DERIVA de esos títulos colapsando los días
 * consecutivos con el mismo título (D01 "Lisboa" + D02 "Lisboa" → Lisboa²).
 *
 * Cuando los títulos no son ciudades limpias —o falta el de algún día—, el
 * número deja de significar algo: la cadena se sigue mostrando pero SIN los
 * días (`withDays: false`), antes que publicar un "2" sobre una frase o un
 * conteo hecho con huecos.
 */
export function itineraryCityChain(
  itinerary: CircuitDay[] | undefined,
  max = ITINERARY_CITY_CHAIN_MAX,
): { stops: ItineraryStop[]; truncated: boolean; withDays: boolean } {
  const days = itinerary ?? [];
  const titles = [...days]
    .sort((a, b) => a.day - b.day)
    .map((day) => day.title?.trim())
    .filter((title): title is string => Boolean(title));

  const stops: ItineraryStop[] = [];
  for (const title of titles) {
    const last = stops[stops.length - 1];
    if (last && last.city.toLowerCase() === title.toLowerCase()) {
      last.days += 1;
      continue;
    }
    stops.push({ city: title, days: 1 });
  }

  return {
    stops: stops.slice(0, max),
    truncated: stops.length > max,
    withDays:
      stops.length > 0 &&
      titles.length === days.length &&
      titles.every(looksLikeCity),
  };
}

/** Frescura del catálogo de un proveedor, ya escrita en el idioma del asesor. */
export interface CatalogFreshnessLine {
  provider: string;
  /** Circuitos rastreados de ese proveedor. */
  count: number;
  /** El mismo número con los separadores de miles del idioma. */
  total: string;
  /** Día y mes del rastreo; con año cuando NO es el año en curso. */
  date: string;
}

/**
 * De cuándo es el catálogo de cada proveedor. Va por proveedor y no como una
 * sola fecha: son rastreos independientes y el asesor tiene que saber con qué
 * fecha trabaja cada mitad de la lista.
 *
 * El año aparece solo cuando la fecha NO es de este año: un rastreo del año
 * pasado escrito "17 ago" se lee como reciente, que es exactamente lo que esta
 * línea existe para evitar.
 */
export function catalogFreshnessLines(
  catalog: CatalogFreshness[] | undefined,
  locale: string,
  now = new Date(),
): CatalogFreshnessLine[] {
  return (catalog ?? []).flatMap((entry) => {
    const fetchedAt = new Date(entry.fetchedAt);
    // El backend ya descarta las fechas que no parsean; si igual llega una,
    // se omite el proveedor antes que pintar "Invalid Date".
    if (Number.isNaN(fetchedAt.getTime())) return [];
    const sameYear = fetchedAt.getFullYear() === now.getFullYear();
    return [
      {
        provider: entry.provider,
        count: entry.total,
        total: entry.total.toLocaleString(locale),
        date: fetchedAt.toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
          ...(sameYear ? {} : { year: 'numeric' }),
        }),
      },
    ];
  });
}

/** Días del circuito según el catálogo (el hub ya validó el número). */
export function catalogDuration(entry: CatalogEntry): number | undefined {
  const days = entry.meta?.durationDays;
  return typeof days === 'number' ? days : undefined;
}

/** El "desde" ya priceado, cuando el catálogo lo trae. */
export function catalogPrice(entry: CatalogEntry): number | undefined {
  const pricing = entry.meta?.pricing as
    | { final?: { amountMinor?: number } }
    | undefined;
  const amount = pricing?.final?.amountMinor;
  return typeof amount === 'number' ? amount : undefined;
}

function catalogCurrency(entry: CatalogEntry): string | undefined {
  const pricing = entry.meta?.pricing as
    | { final?: { currency?: string } }
    | undefined;
  return pricing?.final?.currency;
}

/**
 * Monedas presentes en la grilla. Con más de una, ordenar por precio compara
 * números que no son comparables: la UI lo avisa en vez de fingir un ranking.
 */
export function catalogCurrencies(entries: CatalogEntry[]): string[] {
  return [...new Set(entries.map(catalogCurrency).filter(Boolean) as string[])];
}

export function filterCatalog(
  entries: CatalogEntry[],
  filters: CatalogFilters,
): CatalogEntry[] {
  return entries.filter((entry) => {
    if (
      filters.providers.length &&
      !filters.providers.includes(entry.provider ?? '')
    ) {
      return false;
    }
    if (filters.durations.length) {
      const days = catalogDuration(entry);
      // Sin duración declarada no se puede afirmar que entre en la banda.
      if (days === undefined) return false;
      const bands = DURATION_BANDS.filter((band) =>
        filters.durations.includes(band.key));
      if (!bands.some((band) => days >= band.min && days <= band.max)) {
        return false;
      }
    }
    return true;
  });
}

/** Cuántas cards quedarían con ESTA opción puesta (búsqueda facetada). */
export function catalogFacetCount(
  entries: CatalogEntry[],
  filters: CatalogFilters,
  patch: Partial<CatalogFilters>,
): number {
  return filterCatalog(entries, { ...filters, ...patch }).length;
}

export type CatalogSort = 'name' | 'price' | 'duration';

export function sortCatalog(
  entries: CatalogEntry[],
  sort: CatalogSort,
): CatalogEntry[] {
  const copy = [...entries];
  if (sort === 'price') {
    // Los que todavía no tienen "desde" van al final: no se ordenan como si
    // fueran gratis.
    return copy.sort(
      (a, b) =>
        (catalogPrice(a) ?? Number.MAX_SAFE_INTEGER) -
        (catalogPrice(b) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  if (sort === 'duration') {
    return copy.sort(
      (a, b) =>
        (catalogDuration(a) ?? Number.MAX_SAFE_INTEGER) -
        (catalogDuration(b) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  return copy.sort((a, b) => a.name.localeCompare(b.name));
}

export interface CircuitFilters {
  /** Lujo / Primera / Turista: el precio es DE la categoría. */
  categories: string[];
  /** Meses de salida `YYYY-MM`. */
  months: string[];
}

export const EMPTY_CIRCUIT_FILTERS: CircuitFilters = {
  categories: [],
  months: [],
};

/** Mes de la salida (`2026-09`), para agrupar una ventana larga de fechas. */
export function departureMonth(offer: CircuitOffer): string {
  return (offer.startDate ?? '').slice(0, 7);
}

export function filterDepartures(
  offers: CircuitOffer[],
  filters: CircuitFilters,
): CircuitOffer[] {
  return offers.filter((offer) => {
    if (filters.categories.length && !filters.categories.includes(offer.category ?? '')) {
      return false;
    }
    if (filters.months.length && !filters.months.includes(departureMonth(offer))) {
      return false;
    }
    return true;
  });
}

export function departureFacetCount(
  offers: CircuitOffer[],
  filters: CircuitFilters,
  patch: Partial<CircuitFilters>,
): number {
  return filterDepartures(offers, { ...filters, ...patch }).length;
}

export type CircuitSort = 'date' | 'price';

/**
 * Por fecha por defecto: en un circuito el asesor elige CUÁNDO sale, no qué es
 * (todas las salidas son del mismo producto).
 */
export function sortDeparturesBy(
  offers: CircuitOffer[],
  sort: CircuitSort,
): CircuitOffer[] {
  if (sort === 'price') {
    return [...offers].sort(
      (a, b) => a.pricing.final.amountMinor - b.pricing.final.amountMinor,
    );
  }
  return sortDepartures(offers);
}

/** "2026-09" → "sept 2026". El mes como lo escribe el idioma del asesor. */
export function monthLabel(month: string, locale: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  if (!year || !monthNumber) return month;
  return new Date(year, monthNumber - 1, 1).toLocaleDateString(locale, {
    month: 'short',
    year: 'numeric',
  });
}
